import React, { useEffect, useMemo } from 'react';
import { CircularProgress, InputAdornment, MenuItem, TextField } from '@mui/material';
import { IUser } from '../../../../interfaces/user';
import { ICategoryItem } from '../../categories/interfaces';
import { useGetChildrenCategories } from '../hooks/useGetChildrenCategories';

interface ICategorySelectProps {
  level: number;
  value: string;
  onChange: (level: number, value: string, types?: any[]) => void;
  onBlur: (name: string) => void;
  touched: boolean;
  error: string | undefined;
  disabled: boolean;
  categoriesRoot: ICategoryItem[];
  parentCategoryId: string | null;
  me: IUser;
  label: string;
}

const CategorySelect: React.FC<ICategorySelectProps> = ({
  level,
  value,
  onChange,
  onBlur,
  touched,
  error,
  disabled,
  categoriesRoot,
  parentCategoryId,
  me,
  label,
}) => {
  const { fetch, categories, loading } = useGetChildrenCategories();
  const prevParentIdRef = React.useRef<string | null>(null);

  // Подгружаем категории при изменении parentCategoryId
  useEffect(() => {
    if (level === 0) {
      // Для корневого уровня не нужно загружать
      return;
    }

    // Нормализуем parentId для сравнения - приводим к строке или null
    const normalizedParentId = parentCategoryId ? String(parentCategoryId) : null;

    // Не вызываем fetch если parentId не изменился
    if (normalizedParentId === prevParentIdRef.current) {
      return;
    }

    prevParentIdRef.current = normalizedParentId;

    // Не загружаем если нет родителя
    if (!normalizedParentId) {
      return;
    }

    fetch({ id: Number(normalizedParentId) });
  }, [parentCategoryId, level]);

  const currentLevelCategories = useMemo(() => {
    // Если это корневой уровень - используем categoriesRoot
    if (level === 0) {
      return categoriesRoot;
    }
    // Иначе используем загруженные категории
    return categories || [];
  }, [level, categoriesRoot, categories]);

  const hiddenCategory = (category: ICategoryItem) => {
    if (me.is_admin) return false;
    if (!category.active) return true;
    return false;
  };

  // Если нет категорий для отображения - не рендерим
  if (!currentLevelCategories || currentLevelCategories.length === 0) {
    return null;
  }

  return (
    <TextField
      select
      label={label}
      margin='normal'
      name={`categoryId_${level}`}
      value={value || ''}
      variant='outlined'
      onBlur={() => onBlur(`categoryId_${level}`)}
      onChange={e => {
        const selectedCategory = currentLevelCategories.find(
          item => item.id?.toString() === e.target.value
        );
        const categoryTypes = selectedCategory?.types?.map(t => t.type) || [];
        onChange(level, e.target.value, categoryTypes);
      }}
      disabled={disabled || loading}
      helperText={touched && error}
      error={Boolean(touched && error)}
      InputProps={{
        endAdornment: (
          <InputAdornment position='end' style={{ marginRight: loading ? 24 : 0 }}>
            {loading && <CircularProgress size={20} />}
          </InputAdornment>
        ),
      }}
      SelectProps={{
        IconComponent: loading ? () => null : undefined,
      }}
    >
      {currentLevelCategories.map(item => (
        <MenuItem
          hidden={hiddenCategory(item)}
          key={item.id?.toString()}
          value={item.id?.toString()}
        >
          {item.name}
        </MenuItem>
      ))}
    </TextField>
  );
};

export default CategorySelect;
